import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { getScan } from "../lib/api";

const STEPS = [
  { key: "queued", label: "Queued", at: 5 },
  { key: "fetching", label: "Fetching homepage", at: 10 },
  { key: "privacy", label: "Locating privacy policy", at: 30 },
  { key: "terms", label: "Locating terms of service", at: 50 },
  { key: "cookies", label: "Locating cookie policy", at: 65 },
  { key: "ai", label: "AI risk analysis", at: 75 },
  { key: "done", label: "Complete", at: 100 },
];

export default function ScanProgress() {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const [scan, setScan] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let active = true;
    let interval;

    const poll = async () => {
      try {
        const s = await getScan(scanId);
        if (!active) return;
        setScan(s);
        if (s.status === "done") {
          clearInterval(interval);
          setTimeout(() => navigate(`/report/${s.domain}`), 700);
        } else if (s.status === "error") {
          clearInterval(interval);
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load scan");
        clearInterval(interval);
      }
    };

    poll();
    interval = setInterval(poll, 1500);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [scanId, navigate]);

  const progress = scan?.progress || 0;

  return (
    <main className="border-b border-black min-h-[70vh]" data-testid="scan-progress-page">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF]">
          / Scanning in progress
        </div>
        <h1 className="font-display text-4xl md:text-6xl tracking-tight mt-3" data-testid="scan-domain-heading">
          {scan?.domain || "Initializing"}
        </h1>
        <p className="text-[#475569] mt-3 max-w-xl">
          PrivacyLens is reading your site like a privacy auditor would. Hang tight —
          this usually takes under a minute.
        </p>

        {err && (
          <div className="border border-[#EF4444] bg-[#FEF2F2] text-[#991B1B] p-4 mt-8 flex gap-3" data-testid="scan-error">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <div>
              <div className="font-medium">Scan failed</div>
              <div className="text-sm mt-1">{err}</div>
            </div>
          </div>
        )}

        {scan?.status === "queued" && scan?.queue_position && (
          <div
            className="border border-[#0047FF] bg-[#0047FF]/10 text-[#0A0A0A] p-4 mt-8 flex gap-3"
            data-testid="scan-queue-banner"
          >
            <Loader2 className="w-5 h-5 flex-shrink-0 animate-spin text-[#0047FF]" />
            <div>
              <div className="font-medium">
                Position #{scan.queue_position} in the scan queue
              </div>
              <div className="text-sm mt-1 text-[#475569]">
                A burst of free scans is running right now. Average wait is{" "}
                <strong className="text-[#0A0A0A]">~{scan.queue_position * 30}s</strong>.
                This page will start ticking through the steps automatically once we pick up your scan.
              </div>
            </div>
          </div>
        )}

        {scan?.status === "error" && (
          <div className="border border-[#EF4444] bg-[#FEF2F2] text-[#991B1B] p-4 mt-8 flex gap-3" data-testid="scan-error-state">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <div>
              <div className="font-medium">We couldn't complete the scan</div>
              <div className="text-sm mt-1">{scan.error}</div>
              <button
                onClick={() => navigate("/")}
                className="mt-3 pl-btn-ghost px-4 py-2 text-sm"
                data-testid="scan-back-home"
              >
                Try another URL
              </button>
            </div>
          </div>
        )}

        {/* Progress bar */}
        <div className="mt-12">
          <div className="flex items-center justify-between font-mono text-xs uppercase tracking-widest text-[#475569] mb-2">
            <span>{scan?.progress_step || "Queued"}</span>
            <span data-testid="scan-progress-percent">{progress}%</span>
          </div>
          <div className="h-3 bg-[#F1F5F9] border border-[#0A0A0A] relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-[#0047FF] transition-all duration-700 ease-out"
              style={{ width: `${progress}%` }}
            />
            {scan?.status !== "done" && scan?.status !== "error" && (
              <div className="absolute inset-0 pl-shimmer pointer-events-none" />
            )}
          </div>
        </div>

        {/* Steps */}
        <div className="mt-10 grid sm:grid-cols-2 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
          {STEPS.map((s) => {
            const reached = progress >= s.at;
            const current = scan && scan.progress_step && scan.progress_step.toLowerCase().includes(s.label.toLowerCase().split(" ")[0]);
            return (
              <div key={s.key} className="bg-white p-4 flex items-center gap-3" data-testid={`step-${s.key}`}>
                <span
                  className={`w-8 h-8 grid place-items-center border ${
                    reached
                      ? "bg-[#10B981] border-[#10B981] text-white"
                      : current
                      ? "bg-[#0047FF] border-[#0047FF] text-white"
                      : "bg-white border-[#E2E8F0] text-[#475569]"
                  }`}
                >
                  {reached ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : current ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <span className="font-mono text-[10px]">·</span>
                  )}
                </span>
                <div>
                  <div className="font-medium text-sm">{s.label}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
                    {reached ? "Done" : current ? "Running" : "Pending"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
}
